import React, {useEffect, useState, useRef} from 'react';
import { toPng } from "html-to-image";
import { addNewActivity } from '../../utils/activityLogger';
import RibbonNav from "../RibbonNav";
import DashboardGardenGrid from "../dashboardComponents/DashboardGardenGrid";
import GardenManagerPlantsComponent from "./plantsSection/GardenManagerPlantsComponent";
import GardenManagerRemindersComponent from "./remindersSection/GardenManagerRemindersComponent";
import GardenManagerJournalComponent from "./journalsSection/GardenManagerJournalComponent";
import DashboardCardComponent from "../dashboardComponents/DashboardCardComponent";
import GardenManagerHeaderCard from "./GardenManagerHeaderCard";
import GardenManagerSidebarOverviewComponent from "./GardenManagerSidebarOverviewComponent";
import GardenManagerSidebarRecentActivityComponent from "./GardenManagerSidebarRecentActivityComponent";
import GardenManagerEventPopup from "./GardenManagerEventPopup";
import GardenAlert from "../reusableComponents/GardenAlert";
import GardenManagerSidebarQuickActionsComponent from "./GardenManagerSidebarQuickActionsComponent";
import GardenExportPopup from "./gardenExportComps/GardenExportPopup";
import GardenExportView from "./gardenExportComps/GardenExportView";
import "../../css/gardenManagerStylings/GardenManagerStyling.css";

const GardenManager = () => {

    const [loading, setLoading] = useState(true);
    const [justAllPlants, setJustAllPlants] = React.useState([]);
    const [gardens, setGardens] = React.useState([]);
    const [selectedGarden, setSelectedGarden] = useState(null);
    const [unplantedPlants, setUnplantedPlants] = React.useState([]);
    const [showEventAdder, setShowEventAdder] = React.useState(false);
    const [selectedGardenGridBottom, setSelectedGardenGridBottom] = React.useState('plants');
    const [reminders, setReminders] = React.useState([]);
    const [alerts, setAlerts] = useState([]); // for alerting the user!!
    const [journalEntries, setJournalEntries] = useState([]);
    const [savingPlantDates, setSavingPlantDates] = React.useState(false);

    const [showExportPopup, setShowExportPopup] = useState(false);



    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth <= 768); // Adjust breakpoint as needed
        };

        // Check on mount
        checkIfMobile();

        // Add event listener for resize
        window.addEventListener('resize', checkIfMobile);

        // Cleanup
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    // state for managing planting dates
    const [plantingDates, setPlantingDates] = useState({}); // { plantId: "2025-08-15", ... }
    const [batchDate, setBatchDate] = useState('');

    const getUnplantedPlants = () => {
        if (!selectedGarden || !selectedGarden.plants) {
            setUnplantedPlants([]);
            return;
        }

        const result = selectedGarden.plants.filter(plant => plant.planted_date === null);
        setUnplantedPlants(result);
    }



    //Handles initial load, fGWPO was moved outside of this to be reusable for refreshing and whatnot
    useEffect(() => {
        fetchGardensWithPlantsOptimized();
        fetchReminders();
    }, []);


    // call when selectedGarden changes
    useEffect(() => {
        getUnplantedPlants();

        setPlantingDates({});
        setBatchDate('');
    }, [selectedGarden]);

    // batch plant handler - sets all date pickers to the same date
    const handleBatchPlant = (date) => {
        setBatchDate(date);

        if (date) {
            // create new planting dates object with all unplanted plants set to the batch date
            const newPlantingDates = { ...plantingDates };
            unplantedPlants.forEach(plant => {
                newPlantingDates[plant.id] = date;
            });
            setPlantingDates(newPlantingDates);
        }
    }

    // indiv plant date handler
    const handleSingularDatePlant = (plantId, date) => {
        setPlantingDates(prev => ({
            ...prev,
            [plantId]: date || undefined // remove key if date is empty
        }));
    }

    // save planting dates to db!
    const handleSavePlantingDates = async () => {
        setSavingPlantDates(true);
        try {
            // Filter out empty dates and format for API
            const datesToSave = Object.entries(plantingDates)
                .filter(([plantId, date]) => date && date.trim() !== '')
                .map(([plantId, date]) => {
                    const plant = selectedGarden.plants.find(p => p.id === parseInt(plantId));
                    let expectedHarvestDate = null;

                    if (plant?.plant?.days_to_maturity && date) {
                        const planted = new Date(date);
                        const harvestTime = planted.getTime() + (plant.plant.days_to_maturity * 24 * 60 * 60 * 1000);
                        const harvestDate = new Date(harvestTime);
                        expectedHarvestDate = harvestDate.toISOString().split('T')[0];
                    }

                    return {
                        plant_id: parseInt(plantId),
                        planted_date: date,
                        expected_harvest_date: expectedHarvestDate
                    };
                });

            if (datesToSave.length === 0) {
                showErrorAlert('No Dates Selected', 'Please select at least one planting date before saving.');
                setSavingPlantDates(false);
                return;
            }

            console.log('Saving planting dates:', datesToSave);

            const response = await fetch('/api/gardens/updatePlantingDates', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ plantingDates: datesToSave })
            });

            if (response.ok) {
                // Create activity logs for each planted plant
                for (const plant of datesToSave) {
                    await addNewActivity(
                        'user',
                        'plant',
                        plant.plant_id,
                        'planted',
                        null,
                        null,
                        'planned',
                        'planted',
                        `Planted on ${plant.planted_date}`
                    );
                }

                // Update health ratings
                const healthUpdates = datesToSave.map(plant => ({
                    plant_id: plant.plant_id,
                    health_rating: 8
                }));

                const healthResponse = await fetch('/api/gardenPlants/updatePlantHealthRating', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ plantHealthUpdates: healthUpdates })
                });

                if (!healthResponse.ok) {
                    console.error('Failed to update plant health ratings');
                }

                // Refresh the data
                setPlantingDates({});
                setBatchDate('');

                // Update local state
                const updatedGarden = {
                    ...selectedGarden,
                    plants: selectedGarden.plants.map(plant => {
                        const savedDate = datesToSave.find(d => d.plant_id === plant.id);
                        if (savedDate) {
                            return {
                                ...plant,
                                planted_date: savedDate.planted_date,
                                plant_status: 'planted',
                                health_rating: 8
                            };
                        }
                        return plant;
                    })
                };

                setGardens(prev => prev.map(g =>
                    g.id === selectedGarden.id ? updatedGarden : g
                ));
                setSelectedGarden(updatedGarden);

                await checkAndLogGardenComplete(updatedGarden);

                // SUCCESS ALERT
                showSuccessAlert(
                    'Planting Complete!',
                    `Successfully saved ${datesToSave.length} planting dates and initialized health ratings!`
                );

                await refreshPlants();

            } else {
                const errorData = await response.json();
                console.error('Error saving planting dates:', errorData);
                showErrorAlert(
                    'Save Failed',
                    `Failed to save planting dates: ${errorData.error || 'Unknown error'}`
                );
            }

        } catch (error) {
            console.error('Error saving planting dates:', error);
            showErrorAlert('Save Error', 'An unexpected error occurred while saving planting dates.');
        }

        setSavingPlantDates(false);
    }

    const saveWateringDate = async (garden) => {
        console.log("Watering garden:", garden?.name);

        if (!garden || !garden.id) {
            showErrorAlert('No Garden Selected', 'Please select a garden first before watering.');
            return;
        }

        try {
            const today = new Date().toISOString().split('T')[0];

            const response = await fetch('/api/gardenPlants/waterAllPlants', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    gardenId: garden.id,
                    wateringDate: today
                })
            });

            if (response.ok) {
                const result = await response.json();

                await addNewActivity(
                    'user',
                    'garden',
                    garden.id,
                    'watered',
                    result.data.plantsUpdated,
                    'plants',
                    null,
                    null,
                    `Watered ${result.data.plantsUpdated} plants in ${garden.name}`
                );

                // Update local state
                const updatedGarden = {
                    ...selectedGarden,
                    plants: selectedGarden.plants.map(plant => ({
                        ...plant,
                        last_watered: plant.plant_status !== 'planned' ? today : plant.last_watered
                    }))
                };

                setGardens(prev => prev.map(g =>
                    g.id === selectedGarden.id ? updatedGarden : g
                ));
                setSelectedGarden(updatedGarden);

                // SUCCESS ALERT
                showSuccessAlert(
                    'Watering Complete!',
                    `Successfully watered ${result.data.plantsUpdated} plants in ${garden.name}!`
                );

                await refreshPlants();

            } else {
                const errorData = await response.json();
                console.error('Error watering garden:', errorData);
                showErrorAlert(
                    'Watering Failed',
                    `Error watering garden: ${errorData.error || 'Unknown error'}`
                );
            }
        } catch (error) {
            console.error('Error watering garden:', error);
            showErrorAlert('Watering Error', 'An unexpected error occurred while watering the garden.');
        }
    };


    // function checks if user has completely planted their garden and logs it to db!
    const checkAndLogGardenComplete = async (updatedGarden) => {
        const allPlantsPlanted = updatedGarden.plants.every(plant =>
            plant.planted_date !== null && plant.planted_date !== ''
        );

        if (allPlantsPlanted && updatedGarden.plants.length > 0) {
            await addNewActivity(
                'user',
                'garden',
                updatedGarden.id,
                'completed_planting',
                updatedGarden.plants.length,
                'plants',
                'partially_planted',
                'fully_planted',
                `Garden "${updatedGarden.name}" completely planted with ${updatedGarden.plants.length} plants!`
            );

            // CELEBRATION ALERT - longer duration for special moments
            showSuccessAlert(
                '🎉 Garden Complete!',
                `Congratulations! Garden "${updatedGarden.name}" is now completely planted with ${updatedGarden.plants.length} plants!`,
                10000 // 10 seconds for celebration
            );

        }
    };


    // for grabbing reminders ONCE!
    const fetchReminders = async () => {
        try {
            setLoading(true);
            const response = await fetch('/api/reminder/fetchUserReminders');
            const result = await response.json();

            if (response.ok && result.data) {
                // Sort by reminder_datetime, putting overdue first
                const sortedReminders = result.data.sort((a, b) => {
                    const dateA = new Date(a.reminder_datetime);
                    const dateB = new Date(b.reminder_datetime);
                    const now = new Date();

                    // Overdue reminders first
                    if (dateA < now && dateB >= now) return -1;
                    if (dateB < now && dateA >= now) return 1;

                    return dateA - dateB;
                });
                setReminders(sortedReminders);
            }
        } catch (error) {
            console.error('Error fetching reminders:', error);
            // Fallback with sample data for development
            setReminders([
                {
                    id: 1,
                    title: "Water Tomatoes",
                    description: "Check soil moisture and water if needed",
                    reminder_datetime: "2025-08-07T09:00:00",
                    is_completed: false,
                    priority: 3,
                    tags: ["watering", "vegetables"],
                    plant_id: 1,
                    garden_id: 1
                },
                {
                    id: 2,
                    title: "Fertilize Herbs",
                    description: "Apply organic fertilizer to herb garden",
                    reminder_datetime: "2025-08-08T08:00:00",
                    is_completed: false,
                    priority: 2,
                    tags: ["fertilizing", "herbs"],
                    garden_id: 1
                },
                {
                    id: 3,
                    title: "Harvest Lettuce",
                    description: "Pick outer leaves from lettuce plants",
                    reminder_datetime: "2025-08-06T18:00:00",
                    is_completed: true,
                    priority: 1,
                    tags: ["harvesting", "vegetables"],
                    plant_id: 3
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const refreshReminders = async () => {
        await fetchReminders();
    };

    const refreshJournals = async () => {
        await fetchAllJournalEntries();
    };

    const refreshPlants = async () => {
        await fetchGardensWithPlantsOptimized(true);
    }

    const fetchGardensWithPlantsOptimized = async (preserveSelectedGarden = false) => {
        try {
            setLoading(true);

            // Fetch gardens and all plants in parallel
            const [gardensResponse, plantsResponse] = await Promise.all([
                fetch('/api/gardens/fetchGardens'),
                fetch('/api/gardens/fetchManagerPlants')
            ]);

            const gardensResult = await gardensResponse.json();
            const plantsResult = await plantsResponse.json();

            if (!gardensResponse.ok) {
                console.error('Failed to load gardens:', gardensResult.error);
                return;
            }

            // Group plants by garden_id
            const plantsByGarden = {};
            if (plantsResponse.ok && plantsResult.data) {
                setJustAllPlants(plantsResult.data);
                plantsResult.data.forEach(plant => {
                    if (!plantsByGarden[plant.garden_id]) {
                        plantsByGarden[plant.garden_id] = [];
                    }
                    plantsByGarden[plant.garden_id].push(plant);
                });
            }

            // Combine gardens with their plants
            const gardensWithPlants = gardensResult.data.map(garden => ({
                ...garden,
                plants: plantsByGarden[garden.id] || []
            }));

            setGardens(gardensWithPlants);

            // Update selected garden with fresh data if it exists
            if (preserveSelectedGarden && selectedGarden) {
                const updatedSelectedGarden = gardensWithPlants.find(g => g.id === selectedGarden.id);
                if (updatedSelectedGarden) {
                    setSelectedGarden(updatedSelectedGarden);
                }
            } else if (!selectedGarden) {
                // Only set default if no garden is currently selected
                setSelectedGarden(gardensWithPlants[0]);
            }

            getUnplantedPlants();

        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    };


    const fetchAllJournalEntries = async () => {
        try {
            // setLoading(true);

            const journalResponse = await fetch('/api/journal/fetchJournalEntries');
            const journalResult = await journalResponse.json();

            if (!journalResponse.ok) {
                console.error('Failed to load journal entries:', journalResult.error);
                return;
            }

            setJournalEntries(journalResult.data);

        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            // setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllJournalEntries();
    }, []);



    // ALERT MANAGEMENT FUNCTIONS
    const showAlert = (type, subject, message, duration = 5000) => {
        const newAlert = {
            id: Date.now() + Math.random(), // Ensure unique IDs
            type,
            subject,
            message,
            isVisible: true
        };

        setAlerts(prev => [...prev, newAlert]);

        // Auto-dismiss after specified duration
        if (duration > 0) {
            setTimeout(() => {
                closeAlert(newAlert.id);
            }, duration);
        }

        return newAlert.id; // Return ID in case manual dismissal is needed
    };

    const closeAlert = (id) => {
        setAlerts(prev => prev.filter(alert => alert.id !== id));
    };

    // Helper functions for different alert types
    const showSuccessAlert = (subject, message, duration) =>
        showAlert('Success', subject, message, duration);

    const showErrorAlert = (subject, message, duration = 8000) =>
        showAlert('Error', subject, message, duration);

    const showInfoAlert = (subject, message, duration) =>
        showAlert('Neutral', subject, message, duration);


    const handleShowGridBottomSection = (bottomType) => {
        if (selectedGarden !== null) {
            setSelectedGardenGridBottom(bottomType)
        }
    }



    // stuff for downloading an image of the garden
    const downloadGardenRef = useRef();
    const exportViewRef = useRef(); // NEW REF FOR EXPORT GARDEN VIEW

    // Update the download function to use the export view ref:
    const handleGardenSnapshotDownload = async () => {
        if (!exportViewRef.current) return; // Changed from downloadGardenRef

        try {
            const node = exportViewRef.current; // Changed from downloadGardenRef
            const rect = node.getBoundingClientRect();

            const dataUrl = await toPng(node, {
                width: rect.width,
                height: rect.height,
                pixelRatio: 2,
            });
            const link = document.createElement("a");
            link.download = `${selectedGarden?.name || "my-garden"}.png`;
            link.href = dataUrl;
            link.click();
        } catch (err) {
            console.error("Export failed", err);
        }
    };

    return (
        <div className={`garden-manager`}>
            <RibbonNav pageName={"Garden Manager"} />
            <div className={`garden-manager-body`}>
                <div className="garden-manager-header">
                    <GardenManagerHeaderCard gardens={gardens} selectedGarden={selectedGarden}
                                             setSelectedGarden={setSelectedGarden}
                                             handleWateringGardens={saveWateringDate}
                                             handleAddingEvent={() => setShowEventAdder(true)}
                    />
                </div>

                <div className={`garden-manager-main-content`}>
                    <div className={`garden-grid-section`}>
                        {selectedGarden && (
                            <div
                                ref={downloadGardenRef}
                                key={selectedGarden.id}
                                className="garden-grid-comp"
                            >
                                <DashboardGardenGrid
                                    gardenMatrix={selectedGarden.matrix}
                                    plants={selectedGarden.plants}
                                    gardenManager={true}
                                    isMobile={isMobile}
                                />
                            </div>
                        )}

                        <div className={`garden-manager-bottom-page-selector`}>
                            <div className={'garden-grid-bottom-buttons'}>
                                <button className="garden-grid-bottom-button"
                                        onClick={() => handleShowGridBottomSection("plants")}>
                                    Plants
                                </button>
                                <button className="garden-grid-bottom-button"
                                        onClick={() => handleShowGridBottomSection("reminders")}>
                                    Reminders
                                </button>
                                <button className="garden-grid-bottom-button"
                                        onClick={() => handleShowGridBottomSection("journals")}>
                                    Journals
                                </button>
                            </div>

                            {/*{selectedGarden && (*/}
                            {/*    <div className={`bottom-page-selector-visual-cue`}>*/}
                            {/*        {selectedGardenGridBottom.charAt(0).toUpperCase() + selectedGardenGridBottom.slice(1)} for {selectedGarden.name || 'garden'}*/}
                            {/*    </div>*/}
                            {/*)}*/}
                        </div>

                        <div className={`garden-grid-bottom-content`}>

                            {selectedGardenGridBottom === 'plants' && (
                                <div className={`garden-grid-bottom-plants-content`}>
                                    <GardenManagerPlantsComponent selectedGarden={selectedGarden}/>
                                </div>
                            )}

                            {selectedGardenGridBottom === 'reminders' && (
                                <div className={`garden-grid-bottom-reminders-content`}>
                                    <GardenManagerRemindersComponent
                                        garden={selectedGarden}
                                        plants={selectedGarden.plants}
                                        fetchedReminders={reminders}
                                        onRefreshReminders={refreshReminders}
                                    />
                                </div>
                            )}

                            {selectedGardenGridBottom === 'journals' && (
                                <div className={`garden-grid-bottom-journals-content`}>
                                    <GardenManagerJournalComponent
                                        journals={journalEntries}
                                        garden={selectedGarden}
                                        onRefreshJournals={refreshJournals}
                                    />
                                </div>
                            )}

                        </div>
                    </div>


                    {selectedGarden && (
                        <div className={`garden-sidebar`}>
                            {unplantedPlants.length > 0 ? (
                                // Show Set Planting Dates card when there are unplanted plants
                                <div className={`garden-manager-card`}>
                                    <DashboardCardComponent
                                        title={"📋 Set Planting Dates"}
                                        variant='warning'
                                        gradient={true}
                                    >
                                        <div className={`batch-plant-all`}>
                                            <div className={`batch-plant-message`}>Batch plant:</div>
                                            <input
                                                type="date"
                                                className="garden-date-picker"
                                                title={"Select batch planting date"}
                                                value={batchDate}
                                                onChange={(e) => handleBatchPlant(e.target.value)}
                                            />
                                        </div>
                                        <div className={`set-planting-dates-content`}>
                                            {unplantedPlants.map((plant, index) => (
                                                <div key={plant.id} className={`plant-date-card`}>
                                                    <div className={`date-picker-plant-name`}>
                                                        {plant.plant.plant_name}
                                                    </div>

                                                    <div className={`date-input`}>
                                                        <input
                                                            type="date"
                                                            className="garden-date-picker"
                                                            title={"Select planting date"}
                                                            value={plantingDates[plant.id] || ''}
                                                            onChange={(e) => handleSingularDatePlant(plant.id, e.target.value)}
                                                        />
                                                    </div>

                                                </div>
                                            ))}
                                        </div>
                                        <div className={`save-planting-dates`}>
                                            <button
                                                className={`save-dates-button ${savingPlantDates ? 'disabled' : ''}`}
                                                onClick={handleSavePlantingDates}
                                                disabled={savingPlantDates}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </DashboardCardComponent>
                                </div>
                            ) : (
                                // Show other cards when all plants are planted
                                <>
                                    <div className={`garden-manager-overview-card`}>
                                        <DashboardCardComponent
                                            title={"🏡 Garden Overview"}
                                            variant="primary"
                                            gradient={true}
                                        >
                                            <div className={`sidebar-card-content`}>
                                                <GardenManagerSidebarOverviewComponent garden={selectedGarden}/>
                                            </div>
                                        </DashboardCardComponent>
                                    </div>

                                    <div className={`garden-manager-card`}>
                                        <DashboardCardComponent
                                            title={"⚡ Quick Actions"}
                                            variant="primary"
                                            gradient={true}
                                        >
                                            <div className={`recent-activity-content`}>
                                                <GardenManagerSidebarQuickActionsComponent
                                                    takeSnapshot={() => setShowExportPopup(true)}/>
                                            </div>
                                        </DashboardCardComponent>
                                    </div>

                                    <div className={`garden-manager-card`}>
                                        <DashboardCardComponent
                                            title={"📋 Recent Activity"}
                                            variant="primary"
                                            gradient={true}
                                        >
                                            <div className={`recent-activity-content`}>
                                                <GardenManagerSidebarRecentActivityComponent
                                                    selectedGarden={selectedGarden}/>
                                            </div>
                                        </DashboardCardComponent>
                                    </div>

                                    <div className={`garden-manager-card`}>
                                        <DashboardCardComponent
                                            title={"📣 Community Events"}
                                            variant="primary"
                                            gradient={true}
                                        >
                                            <div className={`garden-overview-content`}>

                                            </div>
                                        </DashboardCardComponent>
                                    </div>

                                </>
                            )}
                        </div>
                    )}

                </div>

                {showEventAdder && (
                    <GardenManagerEventPopup
                        selectedGarden={selectedGarden}
                        gardens={gardens}
                        onClose={() => setShowEventAdder(false)}
                        onAddEvent={() => refreshPlants()}
                    />
                )}

                {alerts.map(alert => (
                    <GardenAlert
                        key={alert.id}
                        alertSubject={alert.subject}
                        alertMessage={alert.message}
                        alertType={alert.type}
                        isVisible={alert.isVisible}
                        onClose={() => closeAlert(alert.id)}
                    />
                ))}

                {showExportPopup && (
                    <GardenExportPopup
                        garden={selectedGarden}
                        isMobile={isMobile}
                        onClose={() => setShowExportPopup(false)}
                    />
                )}

            </div>
        </div>
    )
}

export default GardenManager;